import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { IconManager, coreIconPack } from '@deseretbook/react-ui'
import ReactJWPlayer from 'react-jw-player'
import { useMediaQuery } from '@deseretbook/react-utils'
import { helpers } from '../../../../helpers'

IconManager.register(coreIconPack)

/**
 * Additional content for product page.
 *
 * @returns {function} Component
 */
export const JWPlayerPlaylist = ({
  libraryUrl,
  videoContainerId,
  title,
  playlist = [],
  handleLessonChange,
  activeLesson = {},
}) => {
  // For when the content reaches its tablet breakpoint
  const isTabletOrLarger = useMediaQuery({ minWidth: 767 })
  // let the component know that the player is loaded
  const [playerLoading, setPlayerLoading] = useState(true)

  // JWPlayer will auto-load the first item in the playlist.
  // This is unwanted behavior when we want to load a specific item on page load
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const showNextUp = useRef(isTabletOrLarger)

  // Grabs the jwplayer api from the window
  const playerAPI = window.jwplayer && window.jwplayer(videoContainerId)

  /**
   * Gets the index of the activeLesson within the playlist
   *
   * @returns {number} The index of the activeLesson
   */
  const getPlaylistIndex = () => {
    const lessonIndex = playlist
      .map((playlistLesson) => playlistLesson.mediaid)
      .indexOf(activeLesson.mediaid)

    // Returning -1 would reverse the playlist in the player
    return lessonIndex >= 0 ? lessonIndex : 0
  }

  /**
   * Hides nextup section by setting visibility hidden for the container, thumbnail, and close button
   */
  const hideNextUp = () => {
    if (showNextUp.current) {
      showNextUp.current = false
      const JWPlayer = document.getElementById(videoContainerId)

      helpers.removeClassFromElement('jw-nextup-container-visible', JWPlayer)
    }
  }

  /**
   * Runs whenever a new video is loaded into the player.
   * Allows us to sync up the current state with JW Player
   *
   * @param {object} event - contains the loaded playlist item
   */
  const handleVideoLoad = (event) => {
    // Update the active lesson if its changed
    if (
      (event && event.item && event.item.mediaid) !== (activeLesson && activeLesson.mediaid) &&
      !isInitialLoad
    ) {
      handleLessonChange(event.item)
    }

    // Automatically play the video once it's loaded
    if (playerAPI && playerAPI.play) {
      playerAPI.play()
    }
    const JWPlayer = document.getElementById(videoContainerId)

    // removes the nextup video from view
    helpers.removeClassFromElement('jw-nextup-container-visible', JWPlayer)

    // Re-show the next up section if is larger than tablet size
    if (isTabletOrLarger) {
      showNextUp.current = true
    }

    setIsInitialLoad(false)
  }

  /**
   * Runs whenever the user pauses the video
   *
   * @param {object} _event - info about the current pause action
   */
  const handleOnPause = (_event) => {
    if (showNextUp.current) {
      const JWPlayer = document.getElementById(videoContainerId)

      // Add class to the button to display next up
      helpers.addClassToElement('jw-nextup-container', 'visible', JWPlayer)
    }
  }

  /**
   * Let JWPlayer know which lesson to load when it changes
   *
   * @see https://developer.jwplayer.com/jwplayer/docs/jw8-javascript-api-reference#section-playlist
   */
  const updateActiveLesson = () => {
    if (playerAPI && playerAPI.playlistItem) {
      const lessonIndex = getPlaylistIndex()

      playerAPI.playlistItem(lessonIndex)
    }
  }

  useEffect(() => {
    updateActiveLesson()
  }, [playerAPI, activeLesson])

  // Adds an event listener to the close button on the next up section
  useEffect(() => {
    // We can't select the close button until the player is loaded
    if (!playerLoading) {
      // Add event listener to remove "Next Up" section
      const nextUpCloseButton = document.querySelector('.jw-icon.jw-nextup-close.jw-reset')

      if (nextUpCloseButton) {
        nextUpCloseButton.addEventListener('click', hideNextUp)
      }
    }

    return () => {
      // Remove the event listener for cleanup
      const nextUpCloseButton = document.querySelector('.jw-icon.jw-nextup-close.jw-reset')

      if (nextUpCloseButton) {
        nextUpCloseButton.addEventListener('click', hideNextUp)
      }
    }
  }, [playerLoading])

  return (
    <ReactJWPlayer
      playerId={videoContainerId}
      playerScript={libraryUrl}
      playlist={playlist}
      onVideoLoad={handleVideoLoad}
      onReady={() => {
        setPlayerLoading(false)
        updateActiveLesson()
      }}
      onPause={handleOnPause}
      onResume={() => {
        const JWPlayer = document.getElementById(videoContainerId)

        helpers.removeClassFromElement('jw-nextup-container-visible', JWPlayer)
      }}
      customProps={{
        skin: { name: 'seek' },
        title,
        description: activeLesson.description || '',
      }}
    />
  )
}

JWPlayerPlaylist.propTypes = {
  libraryUrl: PropTypes.string,
  videoContainerId: PropTypes.string,
  title: PropTypes.string,
  playlist: PropTypes.arrayOf(PropTypes.object),
  handleLessonChange: PropTypes.func,
  activeLesson: PropTypes.shape({
    mediaid: PropTypes.string,
    description: PropTypes.string,
  }),
}
